'use client';

import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import { type VariantProps, cva } from 'class-variance-authority';
import * as React from 'react';
import { cn } from '@/lib/utils';
const TooltipProvider = TooltipPrimitive.Provider;
const Tooltip = TooltipPrimitive.Root;
const TooltipTrigger = TooltipPrimitive.Trigger;
const variantOptions = {
  default: 'bg-primary text-primary-foreground',
  outline: 'bg-background text-foreground border border-input',
  secondary: 'bg-secondary text-secondary-foreground',
  destructive: 'bg-destructive text-destructive-foreground',
  muted: 'bg-muted text-muted-foreground',
  none: 'p-0'
};
const tooltipVariants = cva('z-50 overflow-hidden rounded-md px-3 py-1.5 text-xs animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2', {
  variants: {
    variant: variantOptions
  },
  defaultVariants: {
    variant: 'outline'
  }
});
const arrowVariants = cva('', {
  variants: {
    variant: variantOptions
  },
  defaultVariants: {
    variant: 'outline'
  }
});
const TooltipContent = React.forwardRef<React.ElementRef<typeof TooltipPrimitive.Content>, React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content> & VariantProps<typeof tooltipVariants>>(({
  className,
  sideOffset = 4,
  variant,
  ...props
}, ref) => <TooltipPrimitive.Content ref={ref} sideOffset={sideOffset} className={cn(tooltipVariants({
  variant,
  className
}))} {...props} />);
TooltipContent.displayName = TooltipPrimitive.Content.displayName;
const TooltipSimple = React.forwardRef<React.ElementRef<typeof TooltipPrimitive.Content>, Omit<React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Root>, 'children' | 'defaultOpen' | 'open'> & {
  content: React.ReactNode;
  children: React.ReactNode;
  arrow?: boolean;
  variant?: VariantProps<typeof tooltipVariants>['variant'];
  contentProps?: React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>;
  tooltipTriggerProps?: React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Trigger>;
}>(({
  content,
  children,
  variant,
  contentProps,
  tooltipTriggerProps,
  arrow = true,
  ...props
}, ref) => <TooltipProvider>
      <Tooltip {...props}>
        <TooltipTrigger {...tooltipTriggerProps}>{children}</TooltipTrigger>
        <TooltipContent ref={ref} variant={variant} {...contentProps}>
          {arrow && <TooltipPrimitive.TooltipArrow className={cn(arrowVariants({
        variant
      }))} />}
          {content}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>);
TooltipSimple.displayName = 'TooltipSimple';
export { Tooltip, TooltipContent, TooltipProvider, TooltipSimple, TooltipTrigger };